import { Link } from "react-router-dom";
import { ArrowLeft, ScrollText } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { SiteFooter } from "@/components/SiteFooter";
import PageMeta from "@/components/PageMeta";
import LegalPageShell, { LegalSection } from "@/components/legal/LegalPageShell";

const Terms = () => (
  <div className="min-h-screen bg-background text-foreground flex flex-col">
    <PageMeta
      title="Terms of Use — GoToCoins"
      description="Terms of use for GoToCoins: acceptable use, AI-generated content, affiliate links, exchange connections, intellectual property and liability for Australian users."
      path="/terms"
    />
    <main className="flex-1 container mx-auto px-4 py-10 max-w-3xl">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to home
      </Link>

      <LegalPageShell
        icon={ScrollText}
        title="Terms of Use"
        subtitle="The rules that apply when you use GoToCoins."
        lastUpdated="26 August 2026"
      >
        <Card className="border-warning/30 bg-warning/5 mb-8">
          <CardContent className="py-4">
            <p className="text-sm">
              <strong>Draft — pending legal review.</strong> This document is a plain-language
              starting point prepared for the site operator. It has not been reviewed by an
              Australian legal practitioner and should not be relied on as a final legal instrument
              until it has been.
            </p>
          </CardContent>
        </Card>

        <LegalSection title="1. Acceptance">
          <p>
            By accessing or using gotocoin.au and its subdomains (the "Site"), you agree to these
            Terms of Use, the <Link to="/disclaimer" className="underline underline-offset-2">Disclaimer</Link>{" "}
            and the <Link to="/privacy" className="underline underline-offset-2">Privacy Policy</Link>.
            If you do not agree, do not use the Site.
          </p>
          <p>
            The Site is operated from Australia and intended for Australian residents aged 18 or
            over. You are responsible for complying with the laws that apply where you are.
          </p>
        </LegalSection>

        <LegalSection title="2. No financial advice">
          <p>
            All content is general information and education only. GoToCoins does not hold an
            Australian Financial Services Licence and does not provide personal financial product
            advice, tax advice, or legal advice. You make your own investment decisions and bear
            your own losses. See the{" "}
            <Link to="/disclaimer" className="underline underline-offset-2">full disclaimer</Link>.
          </p>
        </LegalSection>

        <LegalSection title="3. AI-generated content">
          <p>
            Scores, reviews, summaries, articles and chat responses on the Site are produced in whole
            or in part by automated systems and large language models. Such content may contain
            errors, omissions, outdated figures, or fabricated detail. Content marked "AI Generated"
            has not been human-reviewed unless it also carries a "Human reviewed" marker.
          </p>
          <p>
            You accept that you use AI-generated output at your own risk and that GoToCoins makes no
            warranty as to its accuracy, completeness, or fitness for any purpose.
          </p>
        </LegalSection>

        <LegalSection title="4. Affiliate links and commercial relationships">
          <p>
            The Site contains affiliate and referral links to cryptocurrency exchanges, including
            Swyftx, CoinSpot and CoinJar. GoToCoins may receive a commission, referral fee or other
            benefit when you register or trade through those links, at no extra cost to you. An
            affiliate disclosure is shown at the point of each such link.
          </p>
          <p>
            Scoring and research outputs are generated from market data independently of these
            commercial relationships. Inclusion of an exchange or token is not an endorsement.
          </p>
        </LegalSection>

        <LegalSection title="5. Exchange connections and API keys">
          <p>
            The Site offers an optional read-only Swyftx portfolio connection. You must only supply
            API credentials with read permissions. Credentials you enter are stored in your own
            browser's local storage and are used solely to request your balances via our proxy; they
            are not persisted on our servers.
          </p>
          <p>
            You are responsible for the security of your exchange account, for revoking keys when
            you stop using the feature, and for complying with your exchange's own terms. GoToCoins
            is not liable for losses arising from your use, misuse, or exposure of API credentials.
          </p>
        </LegalSection>

        <LegalSection title="6. Acceptable use">
          <p>You must not:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>scrape, mirror or bulk-download Site content or data without written permission;</li>
            <li>attempt to bypass rate limits, authentication, or access controls;</li>
            <li>resell, redistribute or present Site content as your own research;</li>
            <li>use the Site to provide financial advice to others; or</li>
            <li>interfere with the Site's operation or security.</li>
          </ul>
        </LegalSection>

        <LegalSection title="7. Accounts and newsletter">
          <p>
            You are responsible for activity under any account you create and for keeping your
            credentials secure. You may unsubscribe from the newsletter at any time using the link in
            any email, or by <Link to="/contact" className="underline underline-offset-2">contacting us</Link>.
          </p>
        </LegalSection>

        <LegalSection title="8. Intellectual property">
          <p>
            The Site, its design, scoring methodologies, and original written content are owned by
            the operator of GoToCoins. Market data remains the property of its respective providers.
            You may quote short extracts with attribution and a link; anything more requires
            permission.
          </p>
        </LegalSection>

        <LegalSection title="9. Availability and changes">
          <p>
            The Site is provided "as is" and "as available". Features, scores, data pipelines and
            these Terms may change or be withdrawn at any time without notice. We will update the
            "last updated" date when these Terms change materially.
          </p>
        </LegalSection>

        <LegalSection title="10. Limitation of liability">
          <p>
            To the maximum extent permitted by law, GoToCoins and its operator are not liable for any
            direct, indirect, incidental or consequential loss (including trading losses, lost
            profits and lost data) arising from your use of the Site. Nothing here excludes,
            restricts or modifies any guarantee, right or remedy under the Australian Consumer Law
            that cannot lawfully be excluded.
          </p>
        </LegalSection>

        <LegalSection title="11. Governing law">
          <p>
            These Terms are governed by the laws of New South Wales, Australia, and you submit to
            the non-exclusive jurisdiction of the courts of that state.
          </p>
        </LegalSection>

        <LegalSection title="12. Contact">
          <p>
            Questions about these Terms can be sent via the{" "}
            <Link to="/contact" className="underline underline-offset-2">contact page</Link>.
          </p>
        </LegalSection>
      </LegalPageShell>
    </main>
    <SiteFooter />
  </div>
);

export default Terms;
